import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { careerTracks } from '../../core/constants/onboarding';

// --- NEW Component: CareerTrackScreen (Step 3) ---
// Third onboarding step. User selects their career track.
export default function CareerTrackScreen({ onComplete, setUserProfile }) {
  const [selectedTrack, setSelectedTrack] = useState(null);
  
  // KINEMATIC TRACKERS: Invisible Baseline Capture
  const mountTimeRef = useRef(null);
  const ttfiCapturedRef = useRef(false);

  // Initialize the baseline clock the moment the DOM renders
  useEffect(() => {
      mountTimeRef.current = Date.now();
  }, []);

  const handleSelectTrack = (trackName) => {
    let capturedBaseline = null;
    
    // Capture strictly the FIRST interaction to establish the cognitive floor
    if (!ttfiCapturedRef.current && mountTimeRef.current) {
        capturedBaseline = Date.now() - mountTimeRef.current;
        ttfiCapturedRef.current = true;
        
        if (process.env.NODE_ENV !== 'production') {
            console.log(`[AEDT Baseline Locked]: ${capturedBaseline}ms`);
        }
    }

    setSelectedTrack(trackName);

    // Push the telemetry silently to the global state & Firestore
    setUserProfile(prev => {
        const updates = { careerTrack: trackName };
        
        if (capturedBaseline) {
            // Nest within psychometrics to maintain root document hygiene
            updates.psychometrics = {
                ...(prev.psychometrics || {}),
                baselineTTFI_ms: capturedBaseline
            };
        }
        
        return { ...prev, ...updates };
    });
  };

  return (
    <div className="max-w-xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in p-8">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-2">Choose Your Career Track</h1>
      <p className="text-lg text-gray-600 text-center mb-8">Select the field that interests you most.</p>
      
      <div className="space-y-4 mb-8">
        {careerTracks.map((track) => {
          const Icon = track.icon;
          const isSelected = selectedTrack === track.name;
          return (
            <button
              key={track.name}
              onClick={() => handleSelectTrack(track.name)}
              className={`w-full text-left p-4 rounded-lg border-2 flex items-center transition duration-200
                ${isSelected 
                  ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-300' 
                  : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
            >
              <Icon className={`w-8 h-8 mr-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
              <div>
                <h4 className={`text-lg font-semibold ${isSelected ? 'text-blue-800' : 'text-gray-900'}`}>{track.name}</h4>
                <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>{track.description}</p>
              </div>
              {isSelected && <CheckCircle className="w-6 h-6 text-blue-600 ml-auto flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      
      <button
        onClick={onComplete}
        disabled={!selectedTrack}
        className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Go to Dashboard
      </button>
    </div>
  );
}